import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '../components/Layout/AdminLayout';
import { PageHeader, StatCard } from '../components/common';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  CircularProgress,
  Alert,
  Typography,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Collapse,
  Paper,
  Chip,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { FileDownload, PictureAsPdf, TrendingUp, TrendingDown, CalendarToday, Assessment, AccountBalanceWallet, Receipt, ShowChart, Percent } from '@mui/icons-material';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import { useRevenueReport, useDownloadRevenueReport } from '../hooks';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts';
import { colors, transitions, radius } from '../theme/theme';

const CHART_COLORS = [
  colors.primary.main,
  colors.secondary.main,
  colors.success.main,
  colors.warning.main,
  colors.info.main,
  colors.error.main,
  '#8884d8',
  '#82ca9d',
  '#ffc658',
  '#ff7c7c',
];

export const RevenueReportPage = () => {
  const [period, setPeriod] = useState('this_month'); // Default to current month
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Calculate dates based on selected period
  const getDateRange = () => {
    const now = new Date();
    let start = '';
    let end = '';

    switch (period) {
      case 'today':
        start = end = now.toISOString().split('T')[0];
        break;
      case 'yesterday':
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        start = end = yesterday.toISOString().split('T')[0];
        break;
      case 'this_week':
        const weekStart = new Date(now);
        weekStart.setDate(now.getDate() - now.getDay());
        start = weekStart.toISOString().split('T')[0];
        end = now.toISOString().split('T')[0];
        break;
      case 'last_week':
        const lastWeekStart = new Date(now);
        lastWeekStart.setDate(now.getDate() - now.getDay() - 7);
        const lastWeekEnd = new Date(lastWeekStart);
        lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
        start = lastWeekStart.toISOString().split('T')[0];
        end = lastWeekEnd.toISOString().split('T')[0];
        break;
      case 'this_month':
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        start = monthStart.toISOString().split('T')[0];
        end = now.toISOString().split('T')[0];
        break;
      case 'last_month':
        const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
        start = lastMonthStart.toISOString().split('T')[0];
        end = lastMonthEnd.toISOString().split('T')[0];
        break;
      case 'this_year':
        const yearStart = new Date(now.getFullYear(), 0, 1);
        start = yearStart.toISOString().split('T')[0];
        end = now.toISOString().split('T')[0];
        break;
      case 'custom':
        start = startDate;
        end = endDate;
        break;
      default:
        start = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
        end = now.toISOString().split('T')[0];
    }

    return { start, end };
  };

  const dateRange = getDateRange();
  const shouldFetch = period !== 'custom' || (!!startDate && !!endDate);

  const { report, isLoading, error } = useRevenueReport(
    { start_date: dateRange.start, end_date: dateRange.end },
    shouldFetch
  );

  const { downloadReport, isDownloading } = useDownloadRevenueReport();

  const handleDownload = async (format: 'pdf' | 'excel' | 'csv') => {
    await downloadReport({ start_date: dateRange.start, end_date: dateRange.end, format });
  };

  // Calculate growth indicators
  const getGrowthIndicator = (growthRate?: number) => {
    if (!growthRate) return null;
    const isPositive = growthRate > 0;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {isPositive ? (
          <TrendingUp sx={{ color: colors.success.main, fontSize: '1.25rem' }} />
        ) : (
          <TrendingDown sx={{ color: colors.error.main, fontSize: '1.25rem' }} />
        )}
        <Typography
          variant="body2"
          sx={{
            color: isPositive ? colors.success.main : colors.error.main,
            fontWeight: 600,
          }}
        >
          {Math.abs(growthRate).toFixed(1)}%
        </Typography>
      </Box>
    );
  };

  return (
    <AdminLayout>
      <Box>
        <PageHeader
          title="Revenue Report"
          subtitle="Comprehensive revenue analysis and insights"
          breadcrumbs={[
            { label: 'Dashboard', path: '/dashboard' },
            { label: 'Reports' },
            { label: 'Revenue Report' },
          ]}
          action={
            <Box display="flex" gap={2}>
              <Button
                variant="outlined"
                startIcon={<FileDownload />}
                onClick={() => handleDownload('excel')}
                disabled={isDownloading || !shouldFetch}
                size="small"
              >
                Excel
              </Button>
              <Button
                variant="outlined"
                startIcon={<PictureAsPdf />}
                onClick={() => handleDownload('pdf')}
                disabled={isDownloading || !shouldFetch}
                size="small"
              >
                PDF
              </Button>
            </Box>
          }
        />

        {/* Period Selector */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarToday sx={{ color: colors.primary.main }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Time Period
                  </Typography>
                </Box>
                <ToggleButtonGroup
                  value={period}
                  exclusive
                  onChange={(e, newPeriod) => newPeriod && setPeriod(newPeriod)}
                  size="small"
                  sx={{
                    flexWrap: 'wrap',
                    '& .MuiToggleButton-root': {
                      textTransform: 'none',
                      fontWeight: 500,
                      px: 2,
                    },
                    '& .Mui-selected': {
                      bgcolor: `${colors.primary.main} !important`,
                      color: 'white !important',
                    },
                  }}
                >
                  <ToggleButton value="today">Today</ToggleButton>
                  <ToggleButton value="yesterday">Yesterday</ToggleButton>
                  <ToggleButton value="this_week">This Week</ToggleButton>
                  <ToggleButton value="last_week">Last Week</ToggleButton>
                  <ToggleButton value="this_month">This Month</ToggleButton>
                  <ToggleButton value="last_month">Last Month</ToggleButton>
                  <ToggleButton value="this_year">This Year</ToggleButton>
                  <ToggleButton value="custom">Custom</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              <Collapse in={period === 'custom'}>
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Start Date"
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="End Date"
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                    />
                  </Grid>
                </Grid>
              </Collapse>
            </Box>
          </CardContent>
        </Card>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load revenue report. Please try again.
          </Alert>
        )}

        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={8}>
            <CircularProgress />
          </Box>
        ) : report ? (
          <>
            {/* Summary Stats */}
            <Grid container spacing={3} mb={3}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <StatCard
                  title="Total Revenue"
                  value={`KES ${report.total_revenue?.toLocaleString() || 0}`}
                  icon={<AccountBalanceWallet />}
                  color={colors.primary.main}
                  trend={report.growth_rate ? {
                    value: `${Math.abs(report.growth_rate).toFixed(1)}%`,
                    isPositive: report.growth_rate > 0
                  } : undefined}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <StatCard
                  title="Total Transactions"
                  value={report.total_transactions?.toLocaleString() || 0}
                  icon={<Receipt />}
                  color={colors.success.main}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <StatCard
                  title="Average Transaction"
                  value={`KES ${report.average_revenue?.toLocaleString() || 0}`}
                  icon={<ShowChart />}
                  color={colors.info.main}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <StatCard
                  title="Growth Rate"
                  value={`${report.growth_rate || 0}%`}
                  icon={<Percent />}
                  color={colors.secondary.main}
                />
              </Grid>
            </Grid>

            {/* Wallet obligation — money held on behalf of customers (a
                liability, kept separate from the revenue figures above). */}
            {report.wallet_obligation && report.wallet_obligation.outstanding > 0 && (
              <Alert severity="info" icon={<AccountBalanceWallet />} sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Wallet obligation:</strong>{' '}
                  KES {report.wallet_obligation.outstanding.toLocaleString(undefined, { maximumFractionDigits: 2 })}{' '}
                  is held in customer wallets (loaded but not yet spent). This is a liability, not
                  revenue, and is excluded from the totals above.{' '}
                  <Link to="/reports/wallet" style={{ color: 'inherit', fontWeight: 600 }}>
                    View wallet report →
                  </Link>
                </Typography>
              </Alert>
            )}

            {/* Revenue by Service - Bar Chart */}
            {report.by_service && report.by_service.length > 0 && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                    Revenue by Service
                  </Typography>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={report.by_service}>
                      <CartesianGrid strokeDasharray="3 3" stroke={colors.grey[200]} />
                      <XAxis
                        dataKey="service_name"
                        angle={-45}
                        textAnchor="end"
                        height={100}
                        tick={{ fontSize: 12 }}
                      />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'white',
                          border: `1px solid ${colors.grey[300]}`,
                          borderRadius: '8px',
                        }}
                        formatter={(value: any) => `KES ${value.toLocaleString()}`}
                      />
                      <Legend />
                      <Bar dataKey="revenue" fill={colors.primary.main} name="Revenue (KES)" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Revenue Distribution - Pie Chart and Payment Methods */}
            <Grid container spacing={3} mb={3}>
              {report.by_service && report.by_service.length > 0 && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                        Service Revenue Distribution
                      </Typography>
                      <ResponsiveContainer width="100%" height={350}>
                        <PieChart>
                          <Pie
                            data={report.by_service}
                            dataKey="revenue"
                            nameKey="service_name"
                            cx="50%"
                            cy="50%"
                            outerRadius={100}
                            label={(entry: any) =>
                              `${entry.name}: ${entry.percent ? (entry.percent * 100).toFixed(1) : 0}%`
                            }
                          >
                            {report.by_service.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value: any) => `KES ${value.toLocaleString()}`} />
                        </PieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {report.by_payment_method && report.by_payment_method.length > 0 && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                        Revenue by Payment Method
                      </Typography>
                      <ResponsiveContainer width="100%" height={350}>
                        <BarChart data={report.by_payment_method} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke={colors.grey[200]} />
                          <XAxis type="number" tick={{ fontSize: 12 }} />
                          <YAxis
                            type="category"
                            dataKey="method"
                            tick={{ fontSize: 12 }}
                            width={100}
                          />
                          <Tooltip
                            formatter={(value: any) => `KES ${value.toLocaleString()}`}
                            contentStyle={{
                              backgroundColor: 'white',
                              border: `1px solid ${colors.grey[300]}`,
                              borderRadius: '8px',
                            }}
                          />
                          <Bar dataKey="revenue" fill={colors.success.main} radius={[0, 8, 8, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>

            {/* Revenue Trend Chart */}
            {report.daily_trend && report.daily_trend.length > 0 && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                    <Assessment sx={{ color: colors.primary.main }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Revenue Trend Analysis
                    </Typography>
                  </Box>
                  <ResponsiveContainer width="100%" height={350}>
                    <AreaChart data={report.daily_trend}>
                      <defs>
                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={colors.primary.main} stopOpacity={0.8}/>
                          <stop offset="95%" stopColor={colors.primary.main} stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={colors.grey[200]} />
                      <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'white',
                          border: `1px solid ${colors.grey[300]}`,
                          borderRadius: '8px',
                        }}
                        formatter={(value: any) => [`KES ${value.toLocaleString()}`, 'Revenue']}
                      />
                      <Area
                        type="monotone"
                        dataKey="revenue"
                        stroke={colors.primary.main}
                        fillOpacity={1}
                        fill="url(#colorRevenue)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Service Revenue DataGrid Table */}
            {report.by_service && report.by_service.length > 0 && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                    Revenue by Service - Detailed Analysis
                  </Typography>
                  <Box sx={{ height: 500, width: '100%' }}>
                    <DataGrid
                      rows={report.by_service.map((item: any, index: number) => ({
                        id: item.service_id || index,
                        ...item,
                      }))}
                      columns={[
                        {
                          field: 'service_name',
                          headerName: 'Service',
                          flex: 1,
                          minWidth: 200,
                          renderCell: (params) => (
                            <Typography variant="body2" fontWeight={600}>
                              {params.value}
                            </Typography>
                          ),
                        },
                        {
                          field: 'transactions',
                          headerName: 'Transactions',
                          width: 140,
                          type: 'number',
                          renderCell: (params) => (
                            <Chip
                              label={params.value?.toLocaleString() || 0}
                              size="small"
                              sx={{ bgcolor: colors.info.light, color: colors.info.dark }}
                            />
                          ),
                        },
                        {
                          field: 'revenue',
                          headerName: 'Revenue (KES)',
                          width: 160,
                          type: 'number',
                          renderCell: (params) => (
                            <Typography variant="body2" fontWeight={700} color={colors.primary.main}>
                              {params.value?.toLocaleString() || 0}
                            </Typography>
                          ),
                        },
                        {
                          field: 'percentage',
                          headerName: '% of Total',
                          width: 120,
                          type: 'number',
                          renderCell: (params) => (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                              <Box
                                sx={{
                                  width: `${params.value || 0}%`,
                                  height: 6,
                                  bgcolor: colors.success.main,
                                  borderRadius: 1,
                                  minWidth: 4,
                                }}
                              />
                              <Typography variant="body2" sx={{ minWidth: 45 }}>
                                {params.value?.toFixed(1) || 0}%
                              </Typography>
                            </Box>
                          ),
                        },
                        {
                          field: 'average_per_transaction',
                          headerName: 'Avg/Transaction',
                          width: 150,
                          type: 'number',
                          renderCell: (params) => (
                            <Typography variant="body2">
                              KES {params.value?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || 0}
                            </Typography>
                          ),
                        },
                      ] as GridColDef[]}
                      slots={{ toolbar: GridToolbar }}
                      slotProps={{
                        toolbar: {
                          showQuickFilter: true,
                          quickFilterProps: { debounceMs: 500 },
                        },
                      }}
                      initialState={{
                        pagination: { paginationModel: { pageSize: 10 } },
                      }}
                      pageSizeOptions={[5, 10, 25, 50]}
                      disableRowSelectionOnClick
                      sx={{
                        '& .MuiDataGrid-cell': {
                          borderBottom: `1px solid ${colors.grey[200]}`,
                        },
                        '& .MuiDataGrid-columnHeaders': {
                          bgcolor: colors.grey[100],
                          fontWeight: 700,
                        },
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Touchpoint Revenue DataGrid Table */}
            {report.by_touchpoint && report.by_touchpoint.length > 0 && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                    Revenue by Touchpoint
                  </Typography>
                  <Box sx={{ height: 400, width: '100%' }}>
                    <DataGrid
                      rows={report.by_touchpoint.map((item: any, index: number) => ({
                        id: item.touchpoint_id || index,
                        ...item,
                      }))}
                      columns={[
                        {
                          field: 'touchpoint',
                          headerName: 'Touchpoint',
                          flex: 1,
                          minWidth: 200,
                          renderCell: (params) => (
                            <Typography variant="body2" fontWeight={600}>
                              {params.value}
                            </Typography>
                          ),
                        },
                        {
                          field: 'transactions',
                          headerName: 'Transactions',
                          width: 140,
                          type: 'number',
                          renderCell: (params) => (
                            <Chip
                              label={params.value?.toLocaleString() || 0}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          ),
                        },
                        {
                          field: 'revenue',
                          headerName: 'Revenue (KES)',
                          width: 160,
                          type: 'number',
                          renderCell: (params) => (
                            <Typography variant="body2" fontWeight={700} color={colors.success.main}>
                              {params.value?.toLocaleString() || 0}
                            </Typography>
                          ),
                        },
                        {
                          field: 'percentage',
                          headerName: '% of Total',
                          width: 150,
                          type: 'number',
                          renderCell: (params) => (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box
                                sx={{
                                  width: 60,
                                  height: 8,
                                  bgcolor: colors.grey[200],
                                  borderRadius: 1,
                                  position: 'relative',
                                  overflow: 'hidden',
                                }}
                              >
                                <Box
                                  sx={{
                                    width: `${params.value || 0}%`,
                                    height: '100%',
                                    bgcolor: colors.primary.main,
                                  }}
                                />
                              </Box>
                              <Typography variant="body2">{params.value?.toFixed(1) || 0}%</Typography>
                            </Box>
                          ),
                        },
                      ] as GridColDef[]}
                      slots={{ toolbar: GridToolbar }}
                      slotProps={{
                        toolbar: {
                          showQuickFilter: true,
                        },
                      }}
                      initialState={{
                        pagination: { paginationModel: { pageSize: 10 } },
                      }}
                      pageSizeOptions={[5, 10, 25]}
                      disableRowSelectionOnClick
                      sx={{
                        '& .MuiDataGrid-cell': {
                          borderBottom: `1px solid ${colors.grey[200]}`,
                        },
                        '& .MuiDataGrid-columnHeaders': {
                          bgcolor: colors.grey[100],
                          fontWeight: 700,
                        },
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Payment Method DataGrid Table */}
            {report.by_payment_method && report.by_payment_method.length > 0 && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                    Revenue by Payment Method
                  </Typography>
                  <Box sx={{ height: 400, width: '100%' }}>
                    <DataGrid
                      rows={report.by_payment_method.map((item: any, index: number) => ({
                        id: index,
                        ...item,
                      }))}
                      columns={[
                        {
                          field: 'method',
                          headerName: 'Payment Method',
                          flex: 1,
                          minWidth: 200,
                          renderCell: (params) => (
                            <Chip
                              label={params.value || 'Unknown'}
                              color="secondary"
                              variant="outlined"
                            />
                          ),
                        },
                        {
                          field: 'transactions',
                          headerName: 'Transactions',
                          width: 140,
                          type: 'number',
                          renderCell: (params) => (
                            <Typography variant="body2" fontWeight={600}>
                              {params.value?.toLocaleString() || 0}
                            </Typography>
                          ),
                        },
                        {
                          field: 'revenue',
                          headerName: 'Revenue (KES)',
                          width: 160,
                          type: 'number',
                          renderCell: (params) => (
                            <Typography variant="body2" fontWeight={700} color={colors.secondary.main}>
                              {params.value?.toLocaleString() || 0}
                            </Typography>
                          ),
                        },
                        {
                          field: 'percentage',
                          headerName: '% of Total',
                          width: 150,
                          type: 'number',
                          renderCell: (params) => (
                            <Chip
                              label={`${params.value?.toFixed(1) || 0}%`}
                              size="small"
                              sx={{
                                bgcolor: colors.secondary.light,
                                color: colors.secondary.dark,
                                fontWeight: 600,
                              }}
                            />
                          ),
                        },
                      ] as GridColDef[]}
                      slots={{ toolbar: GridToolbar }}
                      initialState={{
                        pagination: { paginationModel: { pageSize: 5 } },
                      }}
                      pageSizeOptions={[5, 10]}
                      disableRowSelectionOnClick
                      sx={{
                        '& .MuiDataGrid-cell': {
                          borderBottom: `1px solid ${colors.grey[200]}`,
                        },
                        '& .MuiDataGrid-columnHeaders': {
                          bgcolor: colors.grey[100],
                          fontWeight: 700,
                        },
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* No Data Message */}
            {(!report.by_service || report.by_service.length === 0) &&
             (!report.by_touchpoint || report.by_touchpoint.length === 0) &&
             (!report.by_payment_method || report.by_payment_method.length === 0) &&
             (!report.daily_trend || report.daily_trend.length === 0) && (
              <Card>
                <CardContent>
                  <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                    <Assessment sx={{ fontSize: 64, color: colors.grey[400], mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No Revenue Data Available
                    </Typography>
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                      There are no transactions for the selected period.
                      <br />
                      Try selecting a different date range or check back later.
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            )}
          </>
        ) : (
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="center" alignItems="center" py={8}>
                <Typography color="text.secondary">
                  Select a date range to generate revenue report
                </Typography>
              </Box>
            </CardContent>
          </Card>
        )}
      </Box>
    </AdminLayout>
  );
};
